import React, { useCallback, useEffect, useState } from 'react';

import { LightbulbIcon, SparklesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTitle } from 'react-use';

import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { Switch } from '@/components/ui/switch';
import { useServerPagination } from '@/components/ui/table/useServerPagination';
import useBrains from '@/hooks/useBrains';
import { useDirtyFormBlocker } from '@/hooks/useDirtyFormBlocker';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { PaidFeatureGate } from '@/modules/billing/components/PaidFeatureGate/PaidFeatureGate';
import { PaidFeatureUpsell } from '@/modules/billing/components/PaidFeatureUpsell/PaidFeatureUpsell';
import { EmptyInsights } from '@/modules/insights/components/EmptyInsights';
import { InsightTable } from '@/modules/insights/components/insights-table/Table';
import {
  InsightFilterProvider,
  useInsightFilter,
} from '@/modules/insights/contexts/InsightFilterContext';
import { resetAllFormStates } from '@/redux/forms/actions';
import { selectAnyFormDirty } from '@/redux/forms/selectors';
import { setCurrentInsightId, setIsDuplicatingInsight } from '@/redux/insights/actions';
import { selectCurrentInsightId } from '@/redux/insights/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import useSegments from '@/segment/useSegments';
import { Insight } from '@/shared/types/insights';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

import { useAllInsights, useInsights } from './useInsights';

interface InsightsContentProps {
  toggleSidebar: (sidebar: 'insights' | null) => void;
}

/**
 * Main Insights page content component
 */
const InsightsContent: React.FC<InsightsContentProps> = ({ toggleSidebar }) => {
  const { t } = useTranslation();
  const { brainId } = useParams<{ brainId: string }>();
  const { brain, updateBrain, insightsEnabled } = useBrains(brainId);
  const {
    insights,
    insightsStatus,
    fetchNextPage,
    hasNextPage: serverHasNextPage,
    isFetchingNextPage,
    totalCount,
    deleteInsight,
  } = useInsights(brainId);
  const {
    textFilter,
    typeDirection,
    nameDirection,
    updatedDirection,
    toggleTypeDirection,
    toggleNameDirection,
    toggleUpdatedDirection,
  } = useInsightFilter();
  useDirtyFormBlocker();
  const dispatch = useDispatch();
  const hasAnyFormDirty = useSelector(selectAnyFormDirty);
  const currentInsightId = useSelector(selectCurrentInsightId);

  // Optimistic state for insights toggle
  const [optimisticInsightsEnabled, setOptimisticInsightsEnabled] = useState<boolean | null>(null);

  // Handle insights toggle with optimistic updates
  const handleInsightsToggle = useCallback(
    (checked: boolean) => {
      if (brain) {
        // Update optimistic state immediately
        setOptimisticInsightsEnabled(checked);

        // Perform the actual update
        updateBrain(
          { brain_id: brain.brain_id, metadata: { ...brain.metadata, insights_enabled: checked } },
          {
            onError: () => {
              setOptimisticInsightsEnabled(null);
            },
          }
        );
      }
    },
    [brain, updateBrain]
  );

  // Reset optimistic state when the actual value changes (success case)
  useEffect(() => {
    if (optimisticInsightsEnabled !== null && optimisticInsightsEnabled === insightsEnabled) {
      setOptimisticInsightsEnabled(null);
    }
  }, [insightsEnabled, optimisticInsightsEnabled]);

  // Close sidebar when insights are disabled and in create mode
  useEffect(() => {
    if (!insightsEnabled && !currentInsightId) {
      toggleSidebar(null);
    }
  }, [insightsEnabled, currentInsightId, toggleSidebar]);

  // Use optimistic value if available, otherwise fall back to the actual value
  const displayInsightsEnabled =
    optimisticInsightsEnabled !== null ? optimisticInsightsEnabled : insightsEnabled;

  // Unsaved changes modal state
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [pendingInsightId, setPendingInsightId] = useState<string | null>(null);
  const [pendingActionType, setPendingActionType] = useState<
    'edit' | 'duplicate' | 'create' | null
  >(null);
  const {
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage,
    currentPageData,
    isFetching,
  } = useServerPagination(insights || [], fetchNextPage, {
    dataSelector: (page) => page?.insights || [],
    resetDependencies: [textFilter, typeDirection, nameDirection, updatedDirection, totalCount],
    externalIsFetching: isFetchingNextPage,
    serverHasNextPage,
  });

  /**
   * Handles the actual row click action (switching to the selected insight)
   */
  const handleRowClickAction = useCallback(
    (insightId: string) => {
      toggleSidebar('insights');
      dispatch(setCurrentInsightId(insightId));
    },
    [dispatch, toggleSidebar]
  );

  const onRowClick = useCallback(
    (element: Insight) => {
      // Check if there are any unsaved changes
      if (hasAnyFormDirty) {
        // Store the pending insight ID and action type, then show the modal
        setPendingInsightId(element.insight_id);
        setPendingActionType('edit');
        setIsUnsavedChangesModalOpen(true);
      } else {
        // No unsaved changes, proceed with the action
        handleRowClickAction(element.insight_id);
      }
    },
    [hasAnyFormDirty, handleRowClickAction]
  );

  const onEdit = useCallback(
    (insightId: string) => {
      // Check if there are any unsaved changes
      if (hasAnyFormDirty) {
        // Store the pending insight ID and action type, then show the modal
        setPendingInsightId(insightId);
        setPendingActionType('edit');
        setIsUnsavedChangesModalOpen(true);
      } else {
        // No unsaved changes, proceed with the action
        handleRowClickAction(insightId);
      }
    },
    [hasAnyFormDirty, handleRowClickAction]
  );

  /**
   * Handles the actual duplicate action
   */
  const handleDuplicateAction = useCallback(
    (insightId: string) => {
      dispatch(setIsDuplicatingInsight(true));
      dispatch(setCurrentInsightId(insightId));
      toggleSidebar('insights');
    },
    [dispatch, toggleSidebar]
  );

  /**
   * Handles the actual create action (creating a new insight)
   */
  const handleCreateAction = useCallback(() => {
    dispatch(setCurrentInsightId(null));
    dispatch(setIsDuplicatingInsight(false));
    toggleSidebar('insights');
  }, [dispatch, toggleSidebar]);

  const handleCreateInsight = useCallback(() => {
    // Check if there are any unsaved changes
    if (hasAnyFormDirty) {
      // Show the modal for create action
      setPendingActionType('create');
      setIsUnsavedChangesModalOpen(true);
    } else {
      // No unsaved changes, proceed with creating
      handleCreateAction();
    }
  }, [hasAnyFormDirty, handleCreateAction]);

  const onDuplicate = useCallback(
    (insightId: string) => {
      // Check if there are any unsaved changes
      if (hasAnyFormDirty) {
        // Store the pending insight ID and action type, then show the modal
        setPendingInsightId(insightId);
        setPendingActionType('duplicate');
        setIsUnsavedChangesModalOpen(true);
      } else {
        // No unsaved changes, proceed with the duplication
        handleDuplicateAction(insightId);
      }
    },
    [hasAnyFormDirty, handleDuplicateAction]
  );

  const onDelete = useCallback(
    (insightId: string) => {
      deleteInsight(insightId);
    },
    [deleteInsight]
  );

  // Navigate to the insight details page

  const isLoading = insightsStatus === 'pending';
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  useTitle(t('pages.ai_agent_insights', { 0: brain?.name ?? '-' }));

  // Only show the illustrated empty state when custom insights are enabled; while the toggle is
  // off the table view stays visible so the toggle itself remains reachable.
  const { showEmpty, showEmptySkeleton, showList } = useEmptyStateView({
    scope: `insights:${brainId}`,
    isLoading,
    isEmpty: (totalCount ?? 0) === 0 && textFilter === '' && displayInsightsEnabled,
    isError: insightsStatus === 'error',
  });

  // Unfiltered definition count for the banner (totalCount reflects the active search filter)
  const { data: allInsights } = useAllInsights(brainId);
  const customInsightsCount = allInsights?.length ?? 0;

  const getBannerDescription = () => {
    if (!displayInsightsEnabled) {
      return t('insights.system_banner_description_disabled');
    }
    if (customInsightsCount > 0) {
      return t('insights.system_banner_description_with_custom', { count: customInsightsCount });
    }
    return t('insights.system_banner_description');
  };

  return (
    <div className={TABLE_LAYOUT_FILL_CLASSES}>
      <Alert variant="info" className="mb-4">
        <SparklesIcon />
        <AlertTitle>
          {displayInsightsEnabled
            ? t('insights.system_banner_title')
            : t('insights.system_banner_title_disabled')}
        </AlertTitle>
        <AlertDescription>{getBannerDescription()}</AlertDescription>
      </Alert>
      {(showEmpty || showEmptySkeleton) && (
        <>
          {/* Keep the insights toggle reachable — the empty state replaces the table toolbar */}
          <div className="mb-5 flex justify-end">
            <Switch
              disabled={!canWrite}
              label={t('insights.toggle_label')}
              id="insights-toggle"
              checked={displayInsightsEnabled}
              onCheckedChange={handleInsightsToggle}
              aria-label={t('insights.toggle_label')}
            />
          </div>
          {showEmpty && <EmptyInsights onCreate={handleCreateInsight} />}
          {showEmptySkeleton && <EmptySkeleton media="icon" descriptionLines={4} />}
        </>
      )}

      {showList && (
        <InsightTable
          className="min-h-0 flex-1"
          activeRowId={currentInsightId}
          currentPageIndex={currentPageIndex}
          data={currentPageData}
          emptyStateMessage={t('insights.no_insights_found')}
          fetchNextPage={goToNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetching}
          isLoading={isLoading}
          onCreateInsight={handleCreateInsight}
          insightsEnabled={displayInsightsEnabled}
          onToggleInsights={handleInsightsToggle}
          canWrite={canWrite}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onEdit={onEdit}
          onRowClick={onRowClick}
          readonly={!displayInsightsEnabled}
          rowCount={totalCount}
          setCurrentPageIndex={setCurrentPageIndex}
          typeDirection={typeDirection}
          nameDirection={nameDirection}
          updatedDirection={updatedDirection}
          toggleNameDirection={toggleNameDirection}
          toggleTypeDirection={toggleTypeDirection}
          toggleUpdatedDirection={toggleUpdatedDirection}
        />
      )}

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            // Clear all form states and proceed with the pending action
            dispatch(resetAllFormStates());
            if (pendingActionType === 'create') {
              handleCreateAction();
            } else if (pendingInsightId && pendingActionType) {
              if (pendingActionType === 'duplicate') {
                handleDuplicateAction(pendingInsightId);
              } else {
                handleRowClickAction(pendingInsightId);
              }
            }
            setIsUnsavedChangesModalOpen(false);
            setPendingInsightId(null);
            setPendingActionType(null);
          }}
          onGoBack={() => {
            // Just close the modal and stay on the current form
            setIsUnsavedChangesModalOpen(false);
            setPendingInsightId(null);
            setPendingActionType(null);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </div>
  );
};

interface InsightsProps {
  toggleSidebar: (sidebar: 'insights' | null) => void;
}

/**
 * Main Insights page component. Insights is gated behind a paid plan: trial accounts see a paywall
 * instead of the feature.
 */
export const Insights: React.FC<InsightsProps> = ({ toggleSidebar }) => {
  useSegments(PageName.INSIGHTS);
  const { t } = useTranslation();

  return (
    <PaidFeatureGate
      paywall={
        <PaidFeatureUpsell
          icon={<LightbulbIcon aria-hidden />}
          title={t('insights.paywall_title')}
          description={t('insights.paywall_description')}
        />
      }
    >
      <InsightFilterProvider>
        <InsightsContent toggleSidebar={toggleSidebar} />
      </InsightFilterProvider>
    </PaidFeatureGate>
  );
};
